/**
 * PDF Finalize + Same-Name Export
 * 
 * Purpose: Upload a PDF, optionally edit form fields, finalize (flatten) all
 * interactive elements, and download with the EXACT same filename.
 * 
 * Features:
 * - Upload PDF and analyze for interactive elements
 * - Preview PDF pages
 * - Edit form fields before finalization
 * - Finalize (flatten) to convert interactive elements to static
 * - Download with exact same filename as uploaded
 * - Show file size comparison
 */

import React, { useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import {
  Upload,
  FileText,
  Download,
  Eye,
  Lock,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
  Info,
  Trash2,
  Edit3,
  FileCheck,
  Shield
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PDFFinalize({ onClose }) {
  // Upload state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadId, setUploadId] = useState(null);
  const [pdfInfo, setPdfInfo] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Preview state
  const [currentPage, setCurrentPage] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  
  // Edit state
  const [formEdits, setFormEdits] = useState({});
  const [showEditPanel, setShowEditPanel] = useState(false);
  
  // Finalize state
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [finalizeResult, setFinalizeResult] = useState(null);
  const [showFinalizedPreview, setShowFinalizedPreview] = useState(false);
  
  const fileInputRef = useRef(null);

  // Handle file upload
  const handleFileSelect = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    
    setIsUploading(true);
    setUploadedFile(file);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/api/pdf-finalize/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setUploadId(response.data.upload_id);
        setPdfInfo(response.data);
        setFormEdits({});
        setFinalizeResult(null);
        setCurrentPage(0);
        
        // Load initial preview
        loadPreview(response.data.upload_id, 0, false);
        
        toast.success(`Uploaded: ${response.data.original_filename}`);
      } else {
        throw new Error(response.data.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload PDF');
      setUploadedFile(null);
    } finally {
      setIsUploading(false);
    }
  }, []);

  // Load preview image
  const loadPreview = useCallback(async (id, page, finalized = false) => {
    if (!id) return;
    
    setIsLoadingPreview(true);
    try {
      const response = await axios.get(
        `${API}/api/pdf-finalize/preview/${id}?page=${page}&finalized=${finalized}`,
        { responseType: 'blob' }
      );
      
      const url = URL.createObjectURL(response.data);
      setPreviewUrl(url);
    } catch (error) {
      console.error('Preview error:', error);
      setPreviewUrl(null);
    } finally {
      setIsLoadingPreview(false);
    }
  }, []);

  // Navigate pages
  const goToPrevPage = () => {
    if (currentPage > 0) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      loadPreview(uploadId, newPage, showFinalizedPreview && finalizeResult);
    }
  };

  const goToNextPage = () => {
    if (pdfInfo && currentPage < pdfInfo.page_count - 1) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      loadPreview(uploadId, newPage, showFinalizedPreview && finalizeResult);
    }
  };

  // Handle form field edit
  const handleFieldEdit = (fieldName, value) => {
    setFormEdits(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Apply edits
  const applyEdits = async () => {
    if (!uploadId || Object.keys(formEdits).length === 0) return;
    
    try {
      await axios.post(`${API}/api/pdf-finalize/apply-edits/${uploadId}`, formEdits);
      toast.success('Edits applied');
    } catch (error) {
      toast.error('Failed to apply edits');
    }
  };

  // Finalize PDF
  const handleFinalize = async () => {
    if (!uploadId) return;
    
    setIsFinalizing(true);
    
    try {
      // Apply any pending edits first
      if (Object.keys(formEdits).length > 0) {
        await axios.post(`${API}/api/pdf-finalize/apply-edits/${uploadId}`, formEdits);
      }
      
      // Finalize the PDF
      const response = await axios.post(`${API}/api/pdf-finalize/finalize/${uploadId}?high_quality=true`);
      
      if (response.data.success) {
        setFinalizeResult(response.data);
        setShowFinalizedPreview(true);
        loadPreview(uploadId, currentPage, true);
        toast.success('PDF finalized successfully!');
      } else {
        throw new Error(response.data.error || 'Finalization failed');
      }
    } catch (error) {
      console.error('Finalize error:', error);
      toast.error(error.response?.data?.detail || 'Failed to finalize PDF');
    } finally {
      setIsFinalizing(false);
    }
  };

  // Download finalized PDF
  const handleDownload = async () => {
    if (!uploadId || !finalizeResult) {
      toast.error('Please finalize the PDF first');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/api/pdf-finalize/download/${uploadId}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = pdfInfo.original_filename; // EXACT same filename
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Downloaded: ${pdfInfo.original_filename}`);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  // Reset/Clear
  const handleReset = async () => {
    if (uploadId) {
      try {
        await axios.delete(`${API}/api/pdf-finalize/delete/${uploadId}`);
      } catch (e) {
        // Ignore delete errors
      }
    }
    
    setUploadedFile(null);
    setUploadId(null);
    setPdfInfo(null);
    setFormEdits({});
    setFinalizeResult(null);
    setCurrentPage(0);
    setPreviewUrl(null);
    setShowFinalizedPreview(false);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Toggle preview mode
  const togglePreviewMode = () => {
    if (!finalizeResult) return;
    
    const newMode = !showFinalizedPreview;
    setShowFinalizedPreview(newMode);
    loadPreview(uploadId, currentPage, newMode);
  };

  return (
    <div className="min-h-screen bg-zinc-950 p-6">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Shield className="w-7 h-7 text-purple-500" />
              PDF Finalize + Same-Name Export
            </h1>
            <p className="text-zinc-400 mt-1">
              Upload, finalize, and download a fixed PDF with the exact same filename
            </p>
          </div>
          {onClose && (
            <Button variant="ghost" onClick={onClose} className="text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Upload & Controls */}
        <div className="space-y-4">
          {/* Upload Card */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Upload className="w-5 h-5 text-purple-500" />
                Upload PDF
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!uploadedFile ? (
                <div
                  className="border-2 border-dashed border-zinc-700 rounded-lg p-8 text-center hover:border-purple-500 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-400 mb-2">Drop a PDF file here or click to browse</p>
                  <p className="text-sm text-zinc-600">Supports PDF files with forms, annotations, and interactive elements</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* File Info */}
                  <div className="bg-zinc-800 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="w-10 h-10 text-purple-500" />
                        <div>
                          <p className="font-medium text-white break-all">{pdfInfo?.original_filename}</p>
                          <p className="text-sm text-zinc-400">
                            {pdfInfo?.file_size_formatted} • {pdfInfo?.page_count} page{pdfInfo?.page_count !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={handleReset} className="text-zinc-400 hover:text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Scanned PDF Detection */}
                    {pdfInfo?.is_scanned && (
                      <div className="mt-3 flex items-center gap-2">
                        <Badge className="bg-blue-600/20 text-blue-400 border-blue-600/30">
                          <Eye className="w-3 h-3 mr-1" />
                          Scanned Document Detected
                        </Badge>
                        <span className="text-xs text-zinc-500">
                          OCR will be applied automatically during finalization
                        </span>
                      </div>
                    )}
                    
                    {/* Interactive Elements Badge */}
                    {pdfInfo?.has_interactive_elements ? (
                      <div className="mt-3 flex items-center gap-2">
                        <Badge className="bg-amber-600/20 text-amber-400 border-amber-600/30">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Contains interactive elements
                        </Badge>
                        <span className="text-xs text-zinc-500">
                          {pdfInfo?.form_fields?.length || 0} form fields, {pdfInfo?.annotations?.length || 0} annotations
                        </span>
                      </div>
                    ) : !pdfInfo?.is_scanned && (
                      <div className="mt-3">
                        <Badge className="bg-green-600/20 text-green-400 border-green-600/30">
                          <Check className="w-3 h-3 mr-1" />
                          No interactive elements detected
                        </Badge>
                      </div>
                    )}
                  </div>

                  {isUploading && (
                    <div className="flex items-center gap-3 text-purple-400">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Uploading and analyzing PDF...</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Form Fields Editor */}
          {pdfInfo?.form_fields?.length > 0 && (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Edit3 className="w-5 h-5 text-blue-500" />
                    Edit Form Fields
                    <Badge variant="outline" className="ml-2 text-zinc-400">
                      {pdfInfo.form_fields.length} fields
                    </Badge>
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowEditPanel(!showEditPanel)}
                    className="text-zinc-400"
                  >
                    {showEditPanel ? 'Hide' : 'Show'}
                  </Button>
                </div>
                <CardDescription className="text-zinc-500">
                  Optionally edit form field values before finalization
                </CardDescription>
              </CardHeader>
              {showEditPanel && (
                <CardContent className="space-y-3 max-h-64 overflow-y-auto">
                  {pdfInfo.form_fields.map((field, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <Label className="text-zinc-400 w-32 truncate text-sm" title={field.field_name}>
                        {field.field_name}
                      </Label>
                      <Input
                        value={formEdits[field.field_name] ?? field.value ?? ''}
                        onChange={(e) => handleFieldEdit(field.field_name, e.target.value)}
                        placeholder={field.field_type}
                        className="bg-zinc-800 border-zinc-700 text-white flex-1"
                      />
                      <Badge variant="outline" className="text-xs text-zinc-500">
                        p.{field.page}
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          )}

          {/* Finalize Card */}
          {pdfInfo && (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Lock className="w-5 h-5 text-green-500" />
                  Finalize PDF
                </CardTitle>
                <CardDescription className="text-zinc-500">
                  Convert all interactive elements to static content
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* What gets processed */}
                <div className="bg-zinc-800/50 rounded-lg p-3 border border-zinc-700">
                  <p className="text-sm text-zinc-400 mb-2 flex items-center gap-2">
                    <Info className="w-4 h-4 text-blue-400" />
                    Processing Pipeline:
                  </p>
                  <div className="space-y-2 text-xs text-zinc-400">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs">1</span>
                      <span>Detect if scanned (no text layer)</span>
                    </div>
                    {pdfInfo?.is_scanned && (
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs">2</span>
                        <span>Apply OCR (high accuracy)</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-xs">{pdfInfo?.is_scanned ? '3' : '2'}</span>
                      <span>Rebuild as PDF/A (lock layout)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-xs">{pdfInfo?.is_scanned ? '4' : '3'}</span>
                      <span>Flatten all elements</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-teal-500/20 text-teal-400 flex items-center justify-center text-xs">{pdfInfo?.is_scanned ? '5' : '4'}</span>
                      <span>Preserve original filename: <code className="text-teal-400">{pdfInfo?.original_filename}</code></span>
                    </div>
                  </div>
                </div>

                {/* Finalize Button */}
                {!finalizeResult ? (
                  <Button
                    onClick={handleFinalize}
                    disabled={isFinalizing}
                    className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white py-6"
                  >
                    {isFinalizing ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Finalizing PDF...
                      </>
                    ) : (
                      <>
                        <Lock className="w-5 h-5 mr-2" />
                        Finalize PDF
                      </>
                    )}
                  </Button>
                ) : (
                  <div className="space-y-3">
                    {/* Success Message */}
                    <div className="bg-green-900/20 border border-green-600/30 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-green-400 mb-2">
                        <FileCheck className="w-5 h-5" />
                        <span className="font-medium">PDF Finalized Successfully!</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-zinc-500">Original Size</p>
                          <p className="text-white">{finalizeResult.original_size_formatted}</p>
                        </div>
                        <div>
                          <p className="text-zinc-500">Finalized Size</p>
                          <p className="text-white">
                            {finalizeResult.finalized_size_formatted}
                            <span className={`ml-2 text-xs ${finalizeResult.size_change?.startsWith('+') ? 'text-amber-400' : 'text-green-400'}`}>
                              {finalizeResult.size_change}
                            </span>
                          </p>
                        </div>
                      </div>
                      
                      {/* OCR Info */}
                      {finalizeResult.ocr_applied && (
                        <div className="mt-3 pt-3 border-t border-green-600/20">
                          <div className="flex items-center gap-2 text-blue-400 text-sm">
                            <Eye className="w-4 h-4" />
                            <span>OCR Applied: {finalizeResult.ocr_text_extracted?.toLocaleString() || 0} characters extracted</span>
                          </div>
                        </div>
                      )}
                      
                      {finalizeResult.was_scanned && (
                        <div className="mt-2">
                          <Badge className="bg-blue-500/20 text-blue-400 text-xs">
                            Scanned → Searchable PDF
                          </Badge>
                        </div>
                      )}
                    </div>

                    {/* Download Button */}
                    <Button
                      onClick={handleDownload}
                      className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white py-6"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Download: {pdfInfo.original_filename}
                    </Button>

                    <p className="text-center text-xs text-zinc-500">
                      Downloads with exact same filename as uploaded
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Preview */}
        <div>
          <Card className="bg-zinc-900 border-zinc-800 sticky top-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Eye className="w-5 h-5 text-purple-500" />
                  Preview
                  {finalizeResult && (
                    <Badge className={showFinalizedPreview ? 'bg-green-600' : 'bg-zinc-600'}>
                      {showFinalizedPreview ? 'Finalized' : 'Original'}
                    </Badge>
                  )}
                </CardTitle>
                {finalizeResult && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={togglePreviewMode}
                    className="border-zinc-700 text-zinc-400"
                  >
                    {showFinalizedPreview ? 'View Original' : 'View Finalized'}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!pdfInfo ? (
                <div className="aspect-[8.5/11] bg-zinc-800 rounded-lg flex items-center justify-center">
                  <div className="text-center text-zinc-500">
                    <FileText className="w-16 h-16 mx-auto mb-3 opacity-50" />
                    <p>Upload a PDF to preview</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Preview Image */}
                  <div className="aspect-[8.5/11] bg-zinc-800 rounded-lg overflow-hidden relative">
                    {isLoadingPreview ? (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
                      </div>
                    ) : previewUrl ? (
                      <img
                        src={previewUrl}
                        alt={`Page ${currentPage + 1}`}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-zinc-500">
                        Preview unavailable
                      </div>
                    )}
                  </div>

                  {/* Page Navigation */}
                  {pdfInfo.page_count > 1 && (
                    <div className="flex items-center justify-center gap-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToPrevPage}
                        disabled={currentPage === 0}
                        className="border-zinc-700"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-zinc-400">
                        Page {currentPage + 1} of {pdfInfo.page_count}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToNextPage}
                        disabled={currentPage >= pdfInfo.page_count - 1}
                        className="border-zinc-700"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Info Footer */}
      <div className="max-w-6xl mx-auto mt-6">
        <div className="bg-zinc-900/50 rounded-lg p-4 border border-zinc-800">
          <h4 className="text-sm font-medium text-zinc-300 mb-2">About PDF Finalization</h4>
          <p className="text-xs text-zinc-500">
            Finalization converts all interactive elements (form fields, annotations, text boxes, etc.) 
            into static page content. The resulting PDF looks identical but cannot be edited. 
            The original file is preserved - only the download uses the same filename. 
            This is useful for creating distribution copies of filled forms or signed documents.
          </p>
        </div>
      </div>
    </div>
  );
}
